import { useCallback, useMemo, useRef } from 'react';
import { SubscriptionType } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/subscription_backfill_enums.pb';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { useEligibility } from '@frontend/user-preferences';
import { useNavigate } from '@frontend/weave-router';
import { CALL_INTEL_BASE_URL } from '../../constants';
import { backfillBannerPendoEvents, backfillBannerTrackingIds, trackBackfillBannerEvent } from '../../tracking-ids';

interface Batch1GlobalBannerProps {
  aggregatedSubscriptionType: SubscriptionType;
  onCallIntelligenceRoute: boolean;
}

export const Batch1GlobalBanner = ({
  aggregatedSubscriptionType,
  onCallIntelligenceRoute,
}: Batch1GlobalBannerProps) => {
  const { t } = useTranslation('call-intelligence');
  const navigate = useNavigate();

  const hasFiredPendoRef = useRef(false);

  const { shouldShow, recordDismiss } = useEligibility({
    featureId: 'call-intel-backfill-batch1-banner',
    defaultCriteria: {
      type: 'dismiss-cooldown',
      maxDismisses: 99999,
      cooldownDays: 7,
    },
  });

  const action = useMemo(
    () => ({
      label: t('Go to Call Intelligence'),
      onClick: () => {
        recordDismiss();
        navigate({ to: CALL_INTEL_BASE_URL });
      },
    }),
    [navigate, recordDismiss, t]
  );

  const title = useMemo(() => {
    if (aggregatedSubscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_TRIAL)
      return t('Start Exploring Your Call Intelligence Free Trial!');
    return t('Start Exploring Your Call Intelligence Subscription!');
  }, [aggregatedSubscriptionType, t]);

  const message = useMemo(() => {
    if (aggregatedSubscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_TRIAL) {
      return t("We've finished analyzing your first set of calls to give you quick access to your free trial.");
    }
    return t("We've finished analyzing your first set of calls to give you quick access to your subscription.");
  }, [aggregatedSubscriptionType, t]);

  const handleVisible = useCallback(() => {
    if (!hasFiredPendoRef.current) {
      hasFiredPendoRef.current = true;
      trackBackfillBannerEvent(backfillBannerPendoEvents.batch1BannerSeen, { aggregatedSubscriptionType });
    }
  }, [aggregatedSubscriptionType]);

  if (!shouldShow || onCallIntelligenceRoute) return null;

  return (
    <GlobalBannerDynamic
      type='premium'
      surfaceId='banner.call-intel-backfill'
      id='call-intel-backfill-batch1-ready'
      title={title}
      message={message}
      isDismissible
      onDismiss={recordDismiss}
      onVisible={handleVisible}
      action={action}
      trackingId={backfillBannerTrackingIds.batch1BannerCta}
      dismissBtnTrackingId={backfillBannerTrackingIds.batch1BannerDismiss}
    />
  );
};
